import { useEffect, useRef, useState } from 'react';
import Lottie from 'lottie-react';
import { FaEnvelope, FaRegFileLines, FaWhatsapp } from 'react-icons/fa6';
import whatsappIcon from '../../assets/icons/icon_whatsapp.svg';
import whatsappTabShape from '../../assets/icons/tab_2.svg';
import { getFloatingWhatsappHref, isFloatingWhatsappEnabled } from '../../lib/floatingWhatsapp';
import { ensureSmartlinkLast } from '../../lib/smartlink';
import styles from './FlowChat.module.css';

const MOBILE_BREAKPOINT = '(max-width: 1024px)';
const WELCOME_INTRO_VIDEO_URL = 'https://strapis-wework.jvcsfe.easypanel.host/uploads/Intro_CDMX_1_1_f267fb51ff.webm';
const WELCOME_INTRO_FADE_MS = 650;
const WELCOME_INTRO_SESSION_KEY = 'wework.chat-intro-played';

const hasPlayedWelcomeIntro = () => {
  if (typeof window === 'undefined') return false;

  try {
    return window.sessionStorage.getItem(WELCOME_INTRO_SESSION_KEY) === 'true';
  } catch {
    return false;
  }
};

const rememberWelcomeIntroPlayback = () => {
  try {
    window.sessionStorage.setItem(WELCOME_INTRO_SESSION_KEY, 'true');
  } catch {
    // La reproducciÃ³n sigue funcionando aunque el navegador bloquee el storage.
  }
};

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => (
    typeof window === 'undefined' || window.matchMedia(MOBILE_BREAKPOINT).matches
  ));

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_BREAKPOINT);
    const handleChange = (event) => setIsMobile(event.matches);

    mediaQuery.addEventListener?.('change', handleChange);
    return () => mediaQuery.removeEventListener?.('change', handleChange);
  }, []);

  return isMobile;
};

const createSessionId = () => `koppi-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const clearFlowisePersistedState = (flowId) => {
  if (typeof window === 'undefined' || !flowId) return;

  [window.localStorage, window.sessionStorage].forEach((storage) => {
    const keys = [];
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key && (key === flowId || key.startsWith(`${flowId}_`) || key.includes(flowId))) {
        keys.push(key);
      }
    }
    keys.forEach((key) => storage.removeItem(key));
  });
};

/**
 * Animated Lottie launcher button with rotating tooltip messages.
 * Click toggles the chat (controlled by parent via onClick + isOpen).
 */
const AnimatedChatButton = ({
  animationData,
  onClick,
  isOpen,
  hasBeenOpened,
  messages,
  color,
  side,
  bottomDesktop,
  bottomMobile,
  sizeDesktop,
  sizeMobile,
  sideOffsetDesktop,
  sideOffsetMobile,
}) => {
  const [currentText, setCurrentText] = useState('');
  const containerRef = useRef(null);
  const indexRef = useRef(0);
  const cycleCompletedRef = useRef(false);
  const autoMessagesShownRef = useRef(0);
  const timersRef = useRef([]);
  const spanVisibleRef = useRef(false);
  const isHoveringRef = useRef(false);

  // Timing config (ms)
  const INITIAL_DELAY = 8000;
  const VISIBLE_TIME = 2500;
  const GAP_TIME = 35000;
  const POST_OPEN_DELAY = 30 * 60 * 1000;
  const MAX_AUTO_MESSAGES = 2;

  const clearTimers = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  };
  const addTimer = (id) => { timersRef.current.push(id); };

  const setSpanVisible = (visible) => {
    const span = containerRef.current?.querySelector('span');
    if (!span) return;
    spanVisibleRef.current = visible;
    span.classList.remove(styles.spanHidden, styles.spanVisible);
    span.classList.add(visible ? styles.spanVisible : styles.spanHidden);
  };

  // Main rotation cycle
  useEffect(() => {
    if (isOpen || !messages || messages.length === 0) {
      clearTimers();
      setSpanVisible(false);
      return undefined;
    }

    if (hasBeenOpened) {
      cycleCompletedRef.current = true;
    }

    const scheduleNext = (delay) => {
      if (autoMessagesShownRef.current >= MAX_AUTO_MESSAGES) return;
      addTimer(setTimeout(showNext, delay));
    };

    const showNext = () => {
      if (isHoveringRef.current) return;

      if (autoMessagesShownRef.current >= MAX_AUTO_MESSAGES) return;

      setCurrentText(messages[indexRef.current]);
      autoMessagesShownRef.current += 1;
      setSpanVisible(true);

      addTimer(setTimeout(() => {
        setSpanVisible(false);
        const nextIndex = indexRef.current + 1;

        if (nextIndex >= messages.length) {
          cycleCompletedRef.current = true;
          indexRef.current = 0;
        } else {
          indexRef.current = nextIndex;
        }

        if (autoMessagesShownRef.current >= MAX_AUTO_MESSAGES) return;

        scheduleNext(cycleCompletedRef.current ? POST_OPEN_DELAY : GAP_TIME);
      }, VISIBLE_TIME));
    };

    const firstDelay = hasBeenOpened ? POST_OPEN_DELAY : INITIAL_DELAY;
    scheduleNext(firstDelay);

    return clearTimers;
  }, [messages, isOpen, hasBeenOpened, POST_OPEN_DELAY]);

  const handleMouseEnter = () => {
    if (isOpen || !messages || messages.length === 0) return;
    isHoveringRef.current = true;
    clearTimers();
    indexRef.current = 0;
    setCurrentText(messages[0]);
    setSpanVisible(true);
  };

  const handleMouseLeave = () => {
    if (isOpen || !messages || messages.length === 0) return;
    isHoveringRef.current = false;
    setSpanVisible(false);
    if (autoMessagesShownRef.current >= MAX_AUTO_MESSAGES) return;

    indexRef.current = Math.min(autoMessagesShownRef.current, Math.max(messages.length - 1, 0));

    const scheduleNext = (delay) => {
      if (autoMessagesShownRef.current >= MAX_AUTO_MESSAGES) return;
      addTimer(setTimeout(showNext, delay));
    };

    const showNext = () => {
      if (isHoveringRef.current) return;

      if (autoMessagesShownRef.current >= MAX_AUTO_MESSAGES) return;

      setCurrentText(messages[indexRef.current]);
      autoMessagesShownRef.current += 1;
      setSpanVisible(true);

      addTimer(setTimeout(() => {
        setSpanVisible(false);
        const nextIndex = indexRef.current + 1;

        if (nextIndex >= messages.length) {
          cycleCompletedRef.current = true;
          indexRef.current = 0;
        } else {
          indexRef.current = nextIndex;
        }

        if (autoMessagesShownRef.current >= MAX_AUTO_MESSAGES) return;

        scheduleNext(cycleCompletedRef.current ? POST_OPEN_DELAY : GAP_TIME);
      }, VISIBLE_TIME));
    };

    scheduleNext(GAP_TIME);
  };

  const handleClick = () => {
    clearTimers();
    setSpanVisible(false);
    onClick?.();
  };

  if (!animationData) return null;

  const launcherStyle = {
    ...(color ? { '--chat-accent': color } : null),
    ...(bottomDesktop ? { '--chat-bottom-desktop': bottomDesktop } : null),
    ...(bottomMobile ? { '--chat-bottom-mobile': bottomMobile } : null),
    ...(sizeDesktop ? { '--chat-size-desktop': sizeDesktop } : null),
    ...(sizeMobile ? { '--chat-size-mobile': sizeMobile } : null),
    ...(sideOffsetDesktop ? { '--chat-side-desktop': sideOffsetDesktop } : null),
    ...(sideOffsetMobile ? { '--chat-side-mobile': sideOffsetMobile } : null),
  };

  return (
    <div
      ref={containerRef}
      className={`${styles.launcher} ${isOpen ? styles.launcherOpen : ''}`}
      data-side={side === 'left' ? 'left' : 'right'}
      style={launcherStyle}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <span className={styles.spanHidden}>{currentText}</span>
      <Lottie
        animationData={animationData}
        className={styles.lottie}
        loop
        onClick={handleClick}
      />
    </div>
  );
};

const FloatingWhatsappTab = ({
  whatsappHref,
  side,
  bottomDesktop,
  bottomMobile,
  sideOffsetDesktop,
  sideOffsetMobile,
}) => {
  const tabsStyle = {
    ...(bottomDesktop ? { '--chat-bottom-desktop': bottomDesktop } : null),
    ...(bottomMobile ? { '--chat-bottom-mobile': bottomMobile } : null),
    ...(sideOffsetDesktop ? { '--chat-side-desktop': sideOffsetDesktop } : null),
    ...(sideOffsetMobile ? { '--chat-side-mobile': sideOffsetMobile } : null),
  };
  const resolvedSide = side === 'left' ? 'left' : 'right';

  if (!whatsappHref) return null;

  return (
    <div
      className={`${styles.floatingTabs} ${styles['floatingTabs--whatsapp-only']}`}
      data-side={resolvedSide}
      style={tabsStyle}
      aria-label="Acceso a WhatsApp"
    >
      <div className={styles.floatingTabShell}>
        <a
          href={whatsappHref}
          className={styles.floatingTab}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Abrir WhatsApp"
        >
          <img src={whatsappTabShape} alt="" className={styles.floatingTabShape} />
          <img src={whatsappIcon} alt="" className={styles.floatingTabIcon} />
        </a>
      </div>
    </div>
  );
};

const FlowChat = ({
  companyInfo,
  header,
  smartlink = '',
  smartlinkWhatsapp = '',
  smartlinkEmail = '',
}) => {
  const chatbot = companyInfo?.chatbot;
  const isMobile = useIsMobile();
  const [flowiseComponents, setFlowiseComponents] = useState({ BubbleChat: null, FullPageChat: null });
  const [animationData, setAnimationData] = useState(null);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  const [sessionId, setSessionId] = useState(createSessionId);
  const [introVideoState, setIntroVideoState] = useState(() => (
    hasPlayedWelcomeIntro() ? 'done' : 'idle'
  ));
  const introVideoRef = useRef(null);
  const introStartPendingRef = useRef(false);
  const introFadeTimeoutRef = useRef(null);
  const chatEnabled = Boolean(chatbot?.enabled && chatbot?.flowId);
  const BubbleChatComp = flowiseComponents.BubbleChat;
  const FullPageChatComp = flowiseComponents.FullPageChat;

  // Carga ambos modos: panel completo en escritorio y burbuja en mÃ³vil.
  useEffect(() => {
    if (!chatEnabled) return;
    let cancelled = false;
    import('flowise-embed-react').then((mod) => {
      if (!cancelled) {
        setFlowiseComponents({ BubbleChat: mod.BubbleChat, FullPageChat: mod.FullPageChat });
      }
    });
    return () => { cancelled = true; };
  }, [chatEnabled]);

  useEffect(() => () => {
    if (introFadeTimeoutRef.current) clearTimeout(introFadeTimeoutRef.current);
  }, []);

  useEffect(() => {
    if (isMobile || !chatEnabled || !FullPageChatComp || introVideoState !== 'idle') {
      return undefined;
    }

    const handleFirstPageClick = () => {
      const video = introVideoRef.current;
      if (!video || introStartPendingRef.current) return;

      introStartPendingRef.current = true;
      video.currentTime = 0;
      video.muted = false;
      video.volume = 1;
      setIntroVideoState('playing');

      const playPromise = video.play();
      if (!playPromise?.then) {
        rememberWelcomeIntroPlayback();
        return;
      }

      playPromise
        .then(() => rememberWelcomeIntroPlayback())
        .catch(() => {
          introStartPendingRef.current = false;
          setIntroVideoState('idle');
        });
    };

    document.addEventListener('click', handleFirstPageClick, true);
    return () => document.removeEventListener('click', handleFirstPageClick, true);
  }, [FullPageChatComp, chatEnabled, introVideoState, isMobile]);

  // Load Lottie animation JSON from /public
  useEffect(() => {
    let cancelled = false;
    fetch('/animations/Chatbot_Profile.json')
      .then((res) => res.json())
      .then((json) => { if (!cancelled) setAnimationData(json); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Continuously hide native button + tag bubbles/input with our classes
  // and force Poppins font everywhere (mirrors LST SmartChatbot logic).
  useEffect(() => {
    if (!BubbleChatComp && !FullPageChatComp) return undefined;

    const applyCustomStyles = () => {
      const flowiseElement = document.querySelector('flowise-chatbot, flowise-fullchatbot');

      // Hide original Flowise button (in shadowRoot if present)
      if (flowiseElement?.shadowRoot) {
        const originalButton = flowiseElement.shadowRoot.querySelector('[part="button"]');
        if (originalButton) {
          originalButton.style.display = 'none';
          originalButton.style.visibility = 'hidden';
          originalButton.style.opacity = '0';
          originalButton.style.pointerEvents = 'none';
          originalButton.style.position = 'absolute';
          originalButton.style.left = '-9999px';
          originalButton.style.top = '-9999px';
        }

        const buttonSelectors = [
          '.bubble-button',
          '.chat-button',
          '.chatbot-button',
          'button[data-testid*="button"]',
          'button[aria-label*="chat"]',
          'div[role="button"]',
        ];
        buttonSelectors.forEach((selector) => {
          flowiseElement.shadowRoot.querySelectorAll(selector).forEach((b) => {
            b.style.display = 'none';
            b.style.visibility = 'hidden';
            b.style.opacity = '0';
            b.style.pointerEvents = 'none';
          });
        });
      }

      // Also try main DOM
      [
        'flowise-chatbot [part="button"]',
        '.bubble-button',
        '.chat-button',
        '.chatbot-button',
      ].forEach((selector) => {
        document.querySelectorAll(selector).forEach((b) => {
          b.style.display = 'none';
          b.style.visibility = 'hidden';
          b.style.opacity = '0';
          b.style.pointerEvents = 'none';
        });
      });

      // Tag chat container / bubbles / input with our class names
      const chatContainer =
        document.querySelector('[data-testid="chat-container"]') ||
        document.querySelector('.chat-container') ||
        document.querySelector('.chatbot-container');
      if (chatContainer) chatContainer.classList.add('chatbot-container');

      document
        .querySelectorAll('[data-testid="host-bubble"], .host-bubble')
        .forEach((b) => b.classList.add('chatbot-host-bubble'));
      document
        .querySelectorAll('[data-testid="guest-bubble"], .guest-bubble')
        .forEach((b) => b.classList.add('chatbot-guest-bubble'));

      const chatInput = document.querySelector(
        '[data-testid="chat-input"], .chat-input, input[type="text"], textarea'
      );
      if (chatInput) chatInput.classList.add('chatbot-input');

      // Force Poppins everywhere chat-related
      const applyPoppins = (el) => { el.style.fontFamily = 'Poppins'; };
      const fontSelectors = [
        'flowise-chatbot',
        'flowise-chatbot *',
        '.chatbot-container',
        '.chatbot-container *',
        '[data-testid*="chat"]',
        '[data-testid*="chat"] *',
        '.chat-window',
        '.chat-window *',
        '.bubble-chat',
        '.bubble-chat *',
        '[data-modal="chatbot"]',
        '[data-modal="chatbot"] *',
        '.flowise-fullchatbot',
        '.flowise-fullchatbot *',
        '[role="dialog"]',
        '[role="dialog"] *',
        '.chatbot-widget',
        '.chatbot-widget *',
        '[data-testid*="message"]',
        '[data-testid*="message"] *',
        '.message-content',
        '.message-content *',
        '.bot-message',
        '.bot-message *',
        '.user-message',
        '.user-message *',
      ];
      fontSelectors.forEach((selector) => {
        try {
          document.querySelectorAll(selector).forEach(applyPoppins);
        } catch {
          // ignore invalid selectors
        }
      });

      if (flowiseElement?.shadowRoot) {
        flowiseElement.shadowRoot.querySelectorAll('*').forEach(applyPoppins);
        flowiseElement.shadowRoot.host.style.fontFamily = 'Poppins';
      }

    };

    applyCustomStyles();
    const observer = new MutationObserver(applyCustomStyles);
    observer.observe(document.body, { childList: true, subtree: true });
    const interval = setInterval(applyCustomStyles, 1000);

    return () => {
      observer.disconnect();
      clearInterval(interval);
    };
  }, [BubbleChatComp, FullPageChatComp]);

  const messages = (() => {
    if (!Array.isArray(chatbot?.mensajesRotativos)) return [];
    return chatbot.mensajesRotativos.map((m) => m?.text).filter(Boolean);
  })();

  const whatsappHref = getFloatingWhatsappHref(companyInfo, smartlinkWhatsapp, header);
  const whatsappFloatingEnabled = isFloatingWhatsappEnabled(companyInfo) && Boolean(whatsappHref);
  const side = chatbot?.ladoDerecho === false ? 'left' : 'right';
  const bottomDesktop = chatbot?.posicionBottomDesktop;
  const bottomMobile = chatbot?.posicionBottomMobile;
  const sizeDesktop = chatbot?.tamanoBotonDesktop;
  const sizeMobile = chatbot?.tamanoBotonMobile;
  const sideOffsetDesktop = chatbot?.posicionLateralDesktop;
  const sideOffsetMobile = chatbot?.posicionLateralMobile;

  if (!chatEnabled && !whatsappFloatingEnabled) return null;

  const accent = chatbot?.color || '#ea6387';
  const textColor = chatbot?.textColor || '#ffffff';
  const companyName = companyInfo?.companyName || 'Koppi';

  // Liquid Glass theme (mirrors LST SmartChatbot)
  const chatTheme = {
    button: {
      backgroundColor: accent,
      dragAndDrop: false,
      size: 0,
      iconColor: 'white',
      right: -100,
      bottom: -100,
    },
    tooltip: { showTooltip: false },
    customCSS: `/* Ocultar completamente el botón original de Flowise */
      [part="button"] {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
        width: 0 !important;
        height: 0 !important;
      }

      /* FONT OVERRIDE - Poppins en todos los elementos del chatbot */
      flowise-chatbot *,
      flowise-chatbot,
      .chatbot-container *,
      .chatbot-container,
      [data-testid*="chat"] *,
      [data-testid*="chat"],
      .chat-window *,
      .chat-window,
      .bubble-chat *,
      .bubble-chat {
        font-family: 'Poppins', sans-serif !important;
      }

      /* Contenedor específico del input */
      .chatbot-container .w-full.px-5.pt-2.pb-1 {
        border-top: 2px solid rgba(255, 255, 255, 0.2) !important;
        padding-right: 20px !important;
        padding-left: 0px !important;
        background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05)) !important;
        backdrop-filter: blur(20px) saturate(180%) !important;
        -webkit-backdrop-filter: blur(20px) saturate(180%) !important;
        font-family: 'Poppins', sans-serif !important;
      }

      /* Campo de entrada - Liquid Glass */
      .chatbot-input {
        border-radius: 24px !important;
        margin: 10px !important;
        background: linear-gradient(135deg,
          rgba(255, 255, 255, 0.15) 0%,
          rgba(255, 255, 255, 0.08) 50%,
          rgba(255, 255, 255, 0.12) 100%) !important;
        backdrop-filter: blur(25px) saturate(200%) brightness(1.1) !important;
        -webkit-backdrop-filter: blur(25px) saturate(200%) brightness(1.1) !important;
        border: 1px solid rgba(255, 255, 255, 0.25) !important;
        box-shadow:
          0 8px 32px rgba(0, 0, 0, 0.12),
          inset 0 1px 0 rgba(255, 255, 255, 0.3),
          0 0 0 0.5px rgba(255, 255, 255, 0.1) !important;
        padding: 1px !important;
        color: white !important;
        overflow: hidden !important;
        scrollbar-width: none !important;
        -ms-overflow-style: none !important;
        position: relative !important;
        font-family: 'Poppins', sans-serif !important;
      }

      /* Botones bubble visibles excepto el de envío */
      .bubble-button:not(.flowise-chat-input-send-button),
      .chat-button:not(.flowise-chat-input-send-button),
      .chatbot-button:not(.flowise-chat-input-send-button),
      button[data-testid*="bubble"]:not([data-testid*="send"]),
      button[aria-label*="chat"]:not([aria-label*="send"]),
      div[role="button"]:not(.flowise-chat-input-send-button) {
        display: flex !important;
        visibility: visible !important;
        opacity: 1 !important;
        pointer-events: auto !important;
      }

      /* Estados del input */
      .chatbot-input:hover,
      .chatbot-input:focus-within {
        border-color: ${accent}99 !important;
      }

      /* Ocultar scrollbar */
      .chatbot-input::-webkit-scrollbar { display: none !important; }
      .chatbot-input textarea {
        scrollbar-width: none !important;
        -ms-overflow-style: none !important;
        background: transparent !important;
        position: relative !important;
        z-index: 2 !important;
        font-family: 'Poppins', sans-serif !important;
      }
      .chatbot-input textarea::-webkit-scrollbar { display: none !important; }

      /* Placeholder en blanco */
      .chatbot-input textarea::placeholder,
      .chatbot-input input::placeholder,
      .chatbot-container textarea::placeholder,
      .chatbot-container input::placeholder {
        color: #ffffff !important;
        opacity: 0.7 !important;
        font-family: 'Poppins', sans-serif !important;
      }
      .chatbot-input textarea::-webkit-input-placeholder,
      .chatbot-container textarea::-webkit-input-placeholder {
        color: #ffffff !important;
        opacity: 0.7 !important;
      }

      /* Burbujas del bot */
      .chatbot-host-bubble {
        border-radius: 20px !important;
        padding: 12px 16px !important;
        max-width: 85% !important;
        margin-bottom: 8px !important;
        background-color: rgba(248, 248, 250, 0) !important;
        backdrop-filter: blur(10px) !important;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.36) !important;
        color: white !important;
        border: 1px solid rgba(255, 255, 255, 0) !important;
        font-family: 'Poppins', sans-serif !important;
      }

      /* Burbujas del usuario */
      .chatbot-guest-bubble {
        border-radius: 20px !important;
        padding: 12px 16px !important;
        max-width: 85% !important;
        margin-bottom: 8px !important;
        background-color: ${accent} !important;
        backdrop-filter: blur(10px) !important;
        color: white !important;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
        border: 1px solid rgba(255, 255, 255, 0.2) !important;
        font-family: 'Poppins', sans-serif !important;
      }

      /* Contenedor del chat */
      .chatbot-container {
        border-radius: 10px !important;
        overflow: hidden !important;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
        background-color: rgba(0, 0, 0, 0.3) !important;
        backdrop-filter: blur(15px) !important;
        border: none !important;
        font-family: 'Poppins', sans-serif !important;
      }

      /* Footer "Powered by koppi" - Liquid Glass */
      .chatbot-container span.w-full.text-center,
      .chatbot-container .w-full.text-center.px-[10px] {
        color: rgb(255, 255, 255) !important;
        background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05)) !important;
        backdrop-filter: blur(20px) saturate(180%) !important;
        -webkit-backdrop-filter: blur(20px) saturate(180%) !important;
        font-family: 'Poppins', sans-serif !important;
        border: none !important;
      }

      .chatbot-container span.w-full.text-center a,
      .chatbot-container .w-full.text-center a {
        color: rgb(255, 255, 255) !important;
        font-family: 'Poppins', sans-serif !important;
      }

      .chat-messages-container {
        scroll-behavior: auto !important;
        background-color: transparent !important;
        font-family: 'Poppins', sans-serif !important;
      }

      /* Garantizar Poppins */
      .flowise-fullchatbot *,
      .flowise-fullchatbot,
      [data-modal="chatbot"] *,
      [data-modal="chatbot"],
      .chat-bubble *,
      .chat-bubble,
      .chat-input *,
      .chat-input,
      [role="dialog"] *,
      [role="dialog"],
      .chatbot-widget *,
      .chatbot-widget,
      [data-testid*="message"] *,
      [data-testid*="message"],
      .message-content *,
      .message-content,
      .bot-message *,
      .bot-message,
      .user-message *,
      .user-message,
      [data-testid*="button"] *,
      [data-testid*="button"],
      .send-button *,
      .send-button,
      .attachment-button *,
      .attachment-button {
        font-family: 'Poppins', sans-serif !important;
      }
    `,
    chatWindow: {
      showTitle: true,
      showAgentMessages: false,
      title: chatbot.titulo || `Asistente ${companyName}`,
      titleAvatarSrc: '',
      welcomeMessage:
        chatbot.bienvenida ||
        `¡Hola! Soy tu asistente inteligente de ${companyName}. Estoy aquí para ayudarte con cualquier duda sobre nuestras propiedades, financiamiento, ubicaciones y más. ¿En qué puedo ayudarte hoy?`,
      errorMessage: 'Disculpa, tuve un problema técnico. ¿Podrías intentar de nuevo?',
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      width: 470,
      fontSize: 15,
      starterPrompts: [],
      starterPromptFontSize: 15,
      clearChatOnReload: false,
      sourceDocsTitle: 'Fuentes:',
      renderHTML: true,
      botMessage: {
        backgroundColor: 'rgba(247, 248, 255, 0.1)',
        textColor: '#ffffff',
        showAvatar: false,
        fontFamily: 'Poppins',
      },
      userMessage: {
        backgroundColor: accent,
        textColor: '#ffffff',
        showAvatar: true,
        avatarSrc:
          'https://raw.githubusercontent.com/zahidkhawaja/langchain-chat-nextjs/main/public/usericon.png',
        fontFamily: 'Poppins',
      },
      textInput: {
        placeholder: chatbot.placeholderInput || 'Escribe tu pregunta aquí...',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        textColor: '#ffffff',
        sendButtonColor: 'white',
        maxChars: 500,
        maxCharsWarningMessage: 'Has excedido el límite de caracteres',
        autoFocus: true,
        fontFamily: 'Poppins',
      },
      feedback: {
        color: '#ffffff',
        fontFamily: 'Poppins',
      },
      dateTimeToggle: {
        date: true,
        time: true,
      },
      footer: {
        textColor: '#ffffff',
        text: 'Powered by',
        company: 'koppi',
        companyLink: 'https://koppi.mx',
        fontFamily: 'Poppins',
      },
    },
  };

  const desktopTheme = {
    ...chatTheme,
    customCSS: `
      [part="button"] {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
        width: 0 !important;
        height: 0 !important;
      }

      flowise-fullchatbot,
      flowise-fullchatbot *,
      .chatbot-container,
      .chatbot-container *,
      [data-testid*="chat"],
      [data-testid*="chat"] *,
      [data-testid*="message"],
      [data-testid*="message"] * {
        font-family: 'Poppins', sans-serif !important;
      }

      .chatbot-container {
        border-radius: 0 !important;
        border: 0 !important;
        box-shadow: none !important;
        background: #17191b !important;
        backdrop-filter: none !important;
        -webkit-backdrop-filter: none !important;
        overflow: hidden !important;
      }

      div[style*="max-height: 100dvh"] {
        max-height: 100% !important;
        height: 100% !important;
      }

      .chat-messages-container {
        background: #17191b !important;
        padding: 18px 16px !important;
        scroll-behavior: auto !important;
      }

      .chatbot-container .w-full.px-5.pt-2.pb-1 {
        border-top: 1px solid rgba(255, 255, 255, 0.1) !important;
        padding: 12px 16px !important;
        background: #17191b !important;
        backdrop-filter: none !important;
        -webkit-backdrop-filter: none !important;
      }

      .chatbot-input {
        margin: 0 !important;
        border: 1px solid rgba(255, 255, 255, 0.18) !important;
        border-radius: 12px !important;
        padding: 0 4px !important;
        background: rgba(100, 104, 103, 0.76) !important;
        color: #fff !important;
        box-shadow: none !important;
        backdrop-filter: none !important;
        -webkit-backdrop-filter: none !important;
        overflow: hidden !important;
      }

      .chatbot-input:hover,
      .chatbot-input:focus-within {
        border-color: ${accent}99 !important;
      }

      .chatbot-input textarea,
      .chatbot-input input {
        background: transparent !important;
        color: #fff !important;
      }

      .chatbot-input textarea::placeholder,
      .chatbot-input input::placeholder {
        color: rgba(255, 255, 255, 0.7) !important;
      }

      .chatbot-host-bubble,
      .chatbot-guest-bubble {
        max-width: 85% !important;
        margin-bottom: 8px !important;
        border-radius: 16px !important;
        padding: 12px 16px !important;
        color: #fff !important;
      }

      .chatbot-host-bubble {
        border: 1px solid rgba(255, 255, 255, 0.1) !important;
        background: #242728 !important;
        box-shadow: none !important;
        backdrop-filter: none !important;
      }

      .chatbot-guest-bubble {
        background: #777979 !important;
        backdrop-filter: blur(10px) !important;
      }

      .chatbot-container span.w-full.text-center,
      .chatbot-container .w-full.text-center.px-[10px] {
        border: 0 !important;
        background: #17191b !important;
        color: #fff !important;
        backdrop-filter: none !important;
        -webkit-backdrop-filter: none !important;
      }

      .chatbot-container span.w-full.text-center a,
      .chatbot-container .w-full.text-center a {
        color: #fff !important;
      }
    `,
    chatWindow: {
      ...chatTheme.chatWindow,
      showTitle: false,
      backgroundColor: '#17191b',
      width: '100%',
      height: '100%',
      botMessage: {
        ...chatTheme.chatWindow.botMessage,
        backgroundColor: '#242728',
      },
      userMessage: {
        ...chatTheme.chatWindow.userMessage,
        backgroundColor: '#777979',
      },
      textInput: {
        ...chatTheme.chatWindow.textInput,
        backgroundColor: 'rgba(100, 104, 103, 0.76)',
      },
    },
  };

  const sharedChatProps = {
    chatflowid: chatbot?.flowId,
    apiHost: chatbot?.apiHost || 'https://ecoflow.koppi.mx',
    chatflowConfig: {
      sessionId,
      vars: {
        companyName,
        currentPage: typeof window !== 'undefined' ? window.location.pathname : '/',
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      },
    },
  };

  const companyEmail = companyInfo?.contactInfo?.find((entry) => entry?.type === 'email')?.value || '';
  const advisorEmail = smartlinkEmail && smartlinkEmail !== '-' ? smartlinkEmail : '';
  const emailHref = smartlink
    ? (advisorEmail ? `mailto:${advisorEmail}` : null)
    : (companyEmail ? `mailto:${companyEmail}` : null);
  const contactHref = `/contacto${ensureSmartlinkLast('', smartlink)}`;

  const desktopActions = [
    { id: 'form', label: 'Contacto', href: contactHref, icon: <FaRegFileLines aria-hidden="true" /> },
    { id: 'whatsapp', label: 'WhatsApp', href: whatsappHref, icon: <FaWhatsapp aria-hidden="true" />, external: true },
    { id: 'email', label: 'Correo', href: emailHref, icon: <FaEnvelope aria-hidden="true" /> },
  ].filter((action) => Boolean(action.href));

  const handleIntroVideoEnded = () => {
    setIntroVideoState('fading');
    introFadeTimeoutRef.current = setTimeout(() => {
      setIntroVideoState('done');
      introFadeTimeoutRef.current = null;
    }, WELCOME_INTRO_FADE_MS);
  };

  const handleResetDesktopChat = () => {
    clearFlowisePersistedState(chatbot?.flowId);
    setSessionId(createSessionId());
  };

  // Programmatically open the BubbleChat by clicking its hidden native button
  const handleToggle = () => {
    if (!hasBeenOpened) setHasBeenOpened(true);
    setTimeout(() => {
      const flowiseElement = document.querySelector('flowise-chatbot');
      if (!flowiseElement) return;
      if (flowiseElement.shadowRoot) {
        const btn = flowiseElement.shadowRoot.querySelector('[part="button"]');
        if (btn && typeof btn.click === 'function') {
          btn.click();
          return;
        }
      }
      const flowiseAny = flowiseElement;
      if (typeof flowiseAny.toggle === 'function') flowiseAny.toggle();
      else if (typeof flowiseAny.open === 'function') flowiseAny.open();
      else if (typeof flowiseAny.show === 'function') flowiseAny.show();
    }, 100);
  };

  const introOverlayClassName = [
    styles.welcomeIntroOverlay,
    introVideoState === 'playing' ? styles.welcomeIntroOverlayActive : '',
    introVideoState === 'fading' ? styles.welcomeIntroOverlayFading : '',
  ].filter(Boolean).join(' ');

  return (
    <>
      {isMobile && chatEnabled && (
        <AnimatedChatButton
          animationData={animationData}
          onClick={handleToggle}
          isOpen={false}
          hasBeenOpened={hasBeenOpened}
          messages={messages}
          color={accent}
          side={side}
          bottomDesktop={bottomDesktop}
          bottomMobile={bottomMobile}
          sizeDesktop={sizeDesktop}
          sizeMobile={sizeMobile}
          sideOffsetDesktop={sideOffsetDesktop}
          sideOffsetMobile={sideOffsetMobile}
        />
      )}
      {/* La pestaÃ±a se conserva para mÃ³vil; en escritorio la sustituye el botÃ³n del panel. */}
      {isMobile && whatsappFloatingEnabled && (
        <FloatingWhatsappTab
          whatsappHref={whatsappHref}
          side={side}
          bottomDesktop="30%"
          bottomMobile="30%"
          sideOffsetDesktop={sideOffsetDesktop}
          sideOffsetMobile={sideOffsetMobile}
        />
      )}
      {BubbleChatComp && (
        isMobile ? <BubbleChatComp key={sessionId} {...sharedChatProps} theme={chatTheme} /> : null
      )}
      {!isMobile && chatEnabled && FullPageChatComp && (
        <aside
          className={styles.desktopPanel}
          style={{ '--chat-accent': accent, '--chat-text': textColor }}
          aria-label={chatbot?.titulo || `Asistente ${companyName}`}
        >
          <div className={styles.desktopShell}>
            <div className={styles.desktopCard}>
              <div className={introOverlayClassName} aria-hidden="true">
                <video
                  ref={introVideoRef}
                  className={styles.welcomeIntroVideo}
                  src={WELCOME_INTRO_VIDEO_URL}
                  playsInline
                  preload="auto"
                  disablePictureInPicture
                  onEnded={handleIntroVideoEnded}
                />
              </div>
              <div className={styles.desktopProfile}>
                <div className={styles.desktopAvatar} aria-hidden="true">
                  <Lottie
                    animationData={animationData}
                    loop
                    autoplay
                    className={styles.desktopAvatarLottie}
                  />
                </div>
                <h2 className={styles.desktopTitle}>{chatbot?.titulo || companyName}</h2>
                <p className={styles.desktopRole}>Asistente virtual de {companyName}</p>
              </div>
              {desktopActions.length > 0 && (
                <div className={styles.desktopActions}>
                  {desktopActions.map(({ id, label, href, icon, external }) => (
                    <a
                      key={id}
                      className={styles.desktopActionButton}
                      href={href}
                      target={external ? '_blank' : undefined}
                      rel={external ? 'noopener noreferrer' : undefined}
                      aria-label={label}
                    >
                      <span className={styles.desktopActionIcon}>{icon}</span>
                      <span className={styles.desktopActionLabel}>{label}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>
            <div className={styles.desktopChatFrame}>
              <button
                type="button"
                className={styles.desktopRefreshButton}
                onClick={handleResetDesktopChat}
                aria-label="Reiniciar chat"
                title="Reiniciar chat"
              >
                Reiniciar
              </button>
              <FullPageChatComp
                key={sessionId}
                {...sharedChatProps}
                theme={desktopTheme}
                className={styles.fullPageChatElement}
                style={{ width: '100%', height: '100%' }}
              />
            </div>
          </div>
        </aside>
      )}
    </>
  );
};

export default FlowChat;
